import dayjs from '@/utils/dayjsConfig';
import { FORMATDATE } from '@/enums/pageEnum';
import type {
  AiCallDialogue,
  AiCallVo,
  BulkyRemovalFormData,
} from '@/facilities/modules/aiCall';

/** Jackson serialises LocalDateTime as [y, m, d, h, mi, s, nanos]; ZonedDateTime as epoch millis. */
export const toMillis = (
  ts: AiCallDialogue['timestamp'] | null | undefined,
): number | null => {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === 'number') return ts;
  if (Array.isArray(ts)) {
    const [y, m, d, h = 0, mi = 0, s = 0, nanos = 0] = ts;
    return new Date(y, m - 1, d, h, mi, s, Math.floor(nanos / 1e6)).getTime();
  }
  const parsed = dayjs(ts);
  return parsed.isValid() ? parsed.valueOf() : null;
};

export const formatTs = (
  ts: AiCallDialogue['timestamp'] | null | undefined,
  fmt = FORMATDATE,
) => {
  const ms = toMillis(ts);
  return ms ? dayjs(ms).format(fmt) : '';
};

export const formatDuration = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};

export const isTransferred = (call: AiCallVo | null | undefined) =>
  call?.outcome === 'transferred' ||
  (!!call?.transfer?.to && call?.transfer?.status !== 'expired');

export const TAKEOVER_LIVE_STATUSES = ['requested', 'handoff', 'dialing'];

/** the operator pressed "Take over" and the hand-off has not finished yet (call still live) */
export const takeoverInProgress = (call: AiCallVo | null | undefined) =>
  call?.status === 'IN_PROGRESS' &&
  !!call.transfer?.status &&
  TAKEOVER_LIVE_STATUSES.includes(call.transfer.status);

export const byOperator = (call: AiCallVo | null | undefined) =>
  call?.transfer?.initiatedBy === 'operator';

/** what the takeover is doing right now (requested → handoff → dialing) */
export const takeoverStatusLabel = (call: AiCallVo, t: (k: string) => string) => {
  switch (call.transfer?.status) {
    case 'handoff':
      return t('hotline_takeoverHandoff');
    case 'dialing':
      return t('hotline_takeoverDialing');
    default:
      return t('hotline_takeoverRequested');
  }
};

/** "Transferred to officer +65… · answered · 1m 35s" */
export const transferLabel = (
  call: AiCallVo,
  t: (k: string, o?: Record<string, unknown>) => string,
) => {
  const tr = call.transfer;
  if (!tr?.to) return t('hotline_transferred');
  const operator = tr.requestedByName || t('hotline_operator');
  if (tr.status === 'expired') return t('hotline_takeoverExpired', { name: operator });
  // operator takeover: who answered (phone or browser) does not matter to the reviewer
  const parts = [
    byOperator(call)
      ? t('hotline_takenOverByOcc')
      : `${t('hotline_transferredTo')} ${tr.to}`,
  ];
  switch (tr.status) {
    case 'answered':
      parts.push(t('hotline_transferAnswered'));
      if (typeof tr.durationSeconds === 'number')
        parts.push(formatDuration(tr.durationSeconds));
      break;
    case 'dialing':
      parts.push(t('hotline_transferRinging'));
      break;
    case 'requested':
    case 'handoff':
      parts.push(t('hotline_takeoverRequested'));
      break;
    case 'no-answer':
      parts.push(t('hotline_transferNoAnswer'));
      break;
    case 'failed':
    case 'busy':
    case 'canceled':
      parts.push(t('hotline_transferFailed'));
      break;
    default:
      break;
  }
  return parts.join(' · ');
};

export const callerLabel = (call: AiCallVo) =>
  call.formData?.fullName || call.callerNumber || call.callSid;

export const REQUIRED_BULKY_FIELDS: Array<keyof BulkyRemovalFormData> = [
  'fullName',
  'mobile',
  'address',
  'floor',
  'unit',
  'appointmentDate',
  'items',
  'termsAccepted',
];

export const missingBulkyFields = (form: BulkyRemovalFormData | null | undefined) => {
  if (!form) return [...REQUIRED_BULKY_FIELDS];
  return REQUIRED_BULKY_FIELDS.filter((key) => {
    const v = form[key];
    if (key === 'address')
      return !(form.address && (form.address.full || form.address.postal));
    if (key === 'items') return !(Array.isArray(form.items) && form.items.length > 0);
    if (key === 'termsAccepted') return form.termsAccepted !== true;
    return v === null || v === undefined || String(v).trim() === '';
  });
};

export const addressLabel = (address?: BulkyRemovalFormData['address']) => {
  if (!address) return '';
  if (address.block && address.street) {
    return `${address.block} ${address.street}${address.postal ? ` ${address.postal}` : ''}`;
  }
  return address.full || address.postal || '';
};
